import { useCallback, useMemo } from "react";

import { getMembershipRebuy, MembershipRebuy } from "../helpers/membershipRebuyLink";

import { useTicketingFormV2ContextOrDefaults } from "@/canvas/features/FormV2/contexts/FormV2Context";

type SubmittedBuyer = Partial<
  Record<"firstName" | "lastName" | "email" | "address" | "city" | "postalCode" | "region" | "country", string | null>
>;

export const useMembershipRebuy = (): ((
  submittedBuyer?: SubmittedBuyer,
  submittedPaymentInput?: SubmittedBuyer
) => MembershipRebuy | null) => {
  const { memberInfo, form, formObject } = useTicketingFormV2ContextOrDefaults();

  const addOnRateIds = useMemo(
    () => new Set((formObject.rates ?? []).filter((rate) => rate.isAddOn).map((rate) => rate.id)),
    [formObject.rates]
  );

  return useCallback(
    (submittedBuyer?: SubmittedBuyer, submittedPaymentInput?: SubmittedBuyer) => {
      if (!memberInfo.isEnabled) {
        return null;
      }

      return getMembershipRebuy({
        isMemberInfoEnabled: memberInfo.isEnabled,
        allowsMultipleMemberships: memberInfo.allowsMultipleMemberships,
        ticketsPurchased: form.getValues("ticketsPurchased"),
        addOnRateIds,
        buyer: {
          firstName: submittedBuyer?.firstName ?? form.getValues("firstName"),
          lastName: submittedBuyer?.lastName ?? form.getValues("lastName"),
          email: submittedBuyer?.email ?? form.getValues("email"),
          buyerDetails: {
            // Stripe updates the submitted input, not form state, and the command response omits billing fields.
            address: submittedPaymentInput?.address ?? submittedBuyer?.address ?? form.getValues("address"),
            city: submittedPaymentInput?.city ?? submittedBuyer?.city ?? form.getValues("city"),
            postalCode: submittedPaymentInput?.postalCode ?? submittedBuyer?.postalCode ?? form.getValues("postalCode"),
            region: submittedPaymentInput?.region ?? submittedBuyer?.region ?? form.getValues("region"),
            country: submittedPaymentInput?.country ?? submittedBuyer?.country ?? form.getValues("country"),
            companyName: form.getValues("companyName"),
            giveAsOrganism: form.getValues("giveAsOrganism"),
            customAnswersOfOrder: form.getValues("customAnswersOfOrder"),
          },
        },
      });
    },
    [memberInfo, form, addOnRateIds]
  );
};
